import {sendCustomMetric, type CustomMetricKey} from '@github-ui/stats'
import {useEffect} from 'react'

// Available in newer browsers, but TypeScript doesn't support in our lib version
interface PerformanceMemory {
  usedJSHeapSize: number
  totalJSHeapSize: number
  jsHeapSizeLimit: number
}

export interface PerformanceWithMemory extends Performance {
  memory?: PerformanceMemory
}

/**
 * Gets current JavaScript heap memory usage in megabytes.
 * Only available in Chromium-based browsers.
 */
export function getMemoryMB(): number | null {
  const memory = (performance as PerformanceWithMemory).memory
  if (memory?.usedJSHeapSize) {
    return Math.round((memory.usedJSHeapSize / 1024 / 1024) * 10) / 10
  }
  return null
}

function sendMemoryStats({statName, tags}: {statName: CustomMetricKey; tags?: PlatformBrowserCustomMetricTags}) {
  // eslint-disable-next-line compat/compat
  requestIdleCallback(() => {
    const memory = getMemoryMB()

    if (!memory) return

    sendCustomMetric({
      name: statName,
      value: memory,
      tags,
    })
  })
}

export function useMemoryStats({
  intervalMs = 5000,
  statName,
  tags,
}: {
  intervalMs?: number
  statName: CustomMetricKey
  tags?: PlatformBrowserCustomMetricTags
}) {
  useEffect(() => {
    // This API is not available in all browsers
    if (!performance || !('memory' in performance)) return

    sendMemoryStats({statName, tags})
    const intervalId = setInterval(() => sendMemoryStats({statName, tags}), intervalMs)

    return () => clearInterval(intervalId)
  })
}
